import { settlementFields } from '../src/utils/financialPresentation'
import type { PagamentoFaturaCartao } from '../src/utils/faturasCartao'
import type {
  AcessoBase,
  BaseFinanceira,
  Categoria,
  CartaoCredito,
  Conta,
  Lancamento,
  PerfilAcesso,
  Subcategoria,
  TipoLancamento,
  ContaComSaldo,
  ExtratoContaItem,
  Investimento,
  InvestimentoComSaldo,
  MovimentoInvestimento,
  PosicaoInvestimento,
  Transferencia,
  AuditoriaEvento,
} from '../src/types/database'
import type { createDataClient } from '../src/services/queryClient'
export interface FaturaImportadaForm {
  cartao_id: string
  competencia: string
  vencimento?: string | null
  valor: number
}
export interface ItemFaturaCartaoForm {
  cartao_id: string
  data_compra: string
  descricao: string
  valor: number
  competencia?: string | null
  vencimento?: string | null
  observacao?: string | null
  fatura_id?: string | null
  categoria_id?: string | null
  subcategoria_id?: string | null
  numero_parcela?: number | null
  total_parcelas?: number | null
  gerar_parcelas_futuras?: boolean
  parcela_existente_id?: string | null
}
export interface LancamentoForm {
  id?: string
  tipo: TipoLancamento
  descricao: string
  categoria_id?: string | null
  subcategoria_id?: string | null
  competencia?: string | null
  vencimento?: string | null
  valor_previsto: number
  status?: string
  conta_prevista_id?: string | null
  realizado?: boolean
  valor_realizado?: number
  data_realizacao?: string | null
  conta_realizada_id?: string | null
  codigo_barras?: string | null
  chave_pix?: string | null
  observacoes?: string | null
  pj?: boolean
}
export interface CompraCartaoForm {
  cartao_id: string
  data_compra: string
  descricao: string
  valor_total: number
  quantidade_parcelas: number
  categoria_id?: string | null
  subcategoria_id?: string | null
  observacao?: string | null
  fatura_competencia?: string | null
}
export interface AtualizarCompraCartaoForm extends CompraCartaoForm {
  id: string
}
export interface ExcluirCompraCartaoOpcoes {
  modo?: ModoExclusaoCompraCartao
  numeroParcelaReferencia?: number | null
}
export type ModoExclusaoCompraCartao = 'APENAS_ATUAL' | 'ATUAL_E_FUTURAS' | 'TODAS'
export interface ResultadoExclusaoCompraCartao {
  parcelasExcluidas: number
  compraExcluida: boolean
  parcelasRestantes: number
}
export interface RecorrenciaForm {
  id?: string
  tipo_lancamento: TipoLancamento
  descricao: string
  categoria_id?: string | null
  subcategoria_id?: string | null
  valor: number
  frequencia: string
  dia_referencia?: number | null
  intervalo_dias?: number | null
  data_inicio: string
  data_fim?: string | null
  conta_id?: string | null
  pj?: boolean
  ativa?: boolean
  observacao?: string | null
}
export interface TransferenciaRecorrenciaForm {
  origem_tipo:'CONTA'|'INVESTIMENTO'
  origem_id:string
  destino_tipo:'CONTA'|'INVESTIMENTO'
  destino_id:string
  valor:number
  observacao?:string|null
  frequencia:string
  intervalo_dias?:number|null
  data_inicio:string
  data_fim?:string|null
}

/** Same business rules on client reads and server transactions. */
export function createFinancialOperations(dataClient:ReturnType<typeof createDataClient>){
async function listarContas(baseId: string): Promise<Conta[]> {
  const { data, error } = await dataClient
    .from('contas')
    .select('*')
    .eq('base_id', baseId)
    .order('principal', { ascending: false })
    .order('instituicao', { ascending: true })
    .order('nome', { ascending: true })

  if (error) throw error
  return (data || []) as Conta[]
}

async function salvarLancamento(baseId: string, form: LancamentoForm) {
  if (!form.descricao.trim()) throw new Error('Informe a descrição.')
  if (!form.vencimento) throw new Error('Informe o vencimento.')
  if (!(Number(form.valor_previsto) > 0)) throw new Error('Informe um valor maior que zero.')

  const settlement = settlementFields(form)
  const payload = {
    base_id: baseId,
    tipo: form.tipo,
    descricao: form.descricao.trim(),
    categoria_id: form.categoria_id || null,
    subcategoria_id: form.subcategoria_id || null,
    competencia: form.competencia ? `${form.competencia.slice(0, 7)}-01` : `${form.vencimento.slice(0, 7)}-01`,
    vencimento: form.vencimento,
    valor_previsto: Math.abs(Number(form.valor_previsto || 0)),
    ...settlement,
    conta_prevista_id: form.conta_prevista_id || null,
    codigo_barras: form.tipo === 'DESPESA' ? (form.codigo_barras?.trim() || null) : null,
    chave_pix: form.tipo === 'DESPESA' ? (form.chave_pix?.trim() || null) : null,
    observacoes: form.observacoes?.trim() || null,
    pj: !!form.pj,
    origem: 'WEB_V4',
    atualizado_em: new Date().toISOString(),
  }

  const traduzirErroPagamento = (error: { code?: string; message?: string; details?: string } | null) => {
    if (!error) return null
    const texto = `${error.code || ''} ${error.message || ''} ${error.details || ''}`.toLowerCase()
    if (texto.includes('chave_pix') || texto.includes('schema cache')) {
      return new Error('O banco ainda não está preparado para salvar a chave PIX. Execute a migração 016_chave_pix_lancamentos_v5_5_6.sql no backend legado e tente novamente.')
    }
    return error
  }

  if (form.id) {
    const { data, error } = await dataClient.from('lancamentos').update(payload).eq('id', form.id).eq('base_id', baseId).select('id').single()
    if (error) throw traduzirErroPagamento(error)
    return data.id as string
  }
  const { data, error } = await dataClient.from('lancamentos').insert({ ...payload, criado_em: new Date().toISOString() }).select('id').single()
  if (error) throw traduzirErroPagamento(error)
  return data.id as string
}

async function atualizarLancamentoRecorrenteFuturos(
  baseId: string,
  lancamentoAtual: Lancamento,
  form: LancamentoForm,
) {
  const recorrenciaId = String(lancamentoAtual.recorrencia_id || '').trim()
  if (!recorrenciaId) return 0
  if (String(lancamentoAtual.status || '').toUpperCase() !== 'PENDENTE') {
    throw new Error('A opção “este e futuros” só está disponível para um lançamento pendente.')
  }

  if (!form.descricao.trim()) throw new Error('Informe a descrição.')
  if (!(Number(form.valor_previsto) > 0)) throw new Error('Informe um valor maior que zero.')

  const { data: regra, error: regraError } = await dataClient.from('recorrencias')
    .select('id').eq('base_id', baseId).eq('id', recorrenciaId).maybeSingle()
  if (regraError || !regra) throw new Error('A regra desta recorrência não foi localizada. Nenhuma parcela futura foi alterada.')

  const agora = new Date().toISOString()

  // Atualiza o modelo da recorrência para que ocorrências ainda não geradas
  // também herdem a alteração. A agenda (frequência/datas) é preservada.
  const { data: regraAtualizada, error: recorrenciaError } = await dataClient
    .from('recorrencias')
    .update({
      tipo_lancamento: form.tipo,
      descricao: form.descricao.trim(),
      categoria_id: form.categoria_id || null,
      subcategoria_id: form.subcategoria_id || null,
      valor: Math.abs(Number(form.valor_previsto || 0)),
      conta_id: form.conta_prevista_id || null,
      pj: !!form.pj,
      observacao: form.observacoes?.trim() || null,
      atualizado_em: agora,
    })
    .eq('id', recorrenciaId)
    .eq('base_id', baseId)
    .select('id')
    .maybeSingle()

  if (recorrenciaError) throw recorrenciaError
  if (!regraAtualizada) throw new Error('A regra não foi atualizada. Confira sua permissão; nenhum lançamento futuro foi alterado.')

  const payloadFuturos = {
    tipo: form.tipo,
    descricao: form.descricao.trim(),
    categoria_id: form.categoria_id || null,
    subcategoria_id: form.subcategoria_id || null,
    valor_previsto: Math.abs(Number(form.valor_previsto || 0)),
    conta_prevista_id: form.conta_prevista_id || null,
    observacoes: form.observacoes?.trim() || null,
    pj: !!form.pj,
    atualizado_em: agora,
  }

  // Somente ocorrências futuras e ainda pendentes são alteradas.
  // Datas/competências já programadas não são reescritas para evitar
  // duplicidade de recorrência e preservar o histórico cronológico.
  let query = dataClient
    .from('lancamentos')
    .update(payloadFuturos)
    .eq('base_id', baseId)
    .eq('recorrencia_id', recorrenciaId)
    .eq('status', 'PENDENTE')

  const referenciaAtual = String(lancamentoAtual.recorrencia_referencia || '').slice(0, 10)
  if (referenciaAtual) query = query.gt('recorrencia_referencia', referenciaAtual)
  else if (lancamentoAtual.vencimento) query = query.gt('vencimento', String(lancamentoAtual.vencimento).slice(0, 10))
  else query = query.neq('id', lancamentoAtual.id)

  const { data, error } = await query.select('id')
  if (error) throw error
  return data?.length || 0
}

async function listarInvestimentos(baseId: string): Promise<Investimento[]> {
  const { data, error } = await dataClient
    .from('investimentos')
    .select('*')
    .eq('base_id', baseId)
    .order('instituicao', { ascending: true })
    .order('nome', { ascending: true })
  if (error) throw error
  return (data || []) as Investimento[]
}

function isoHojeLocal() {
  const parts = new Intl.DateTimeFormat('en-CA', {timeZone:'America/Sao_Paulo',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(new Date())
  const value=(type:string)=>parts.find(p=>p.type===type)?.value || ''
  return `${value('year')}-${value('month')}-${value('day')}`
}

function parseISOData(v: string) {
  const [y, m, d] = v.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12))
}

function dataISO(d: Date) {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
}

function adicionarDias(v: string, dias: number) {
  const d = parseISOData(v); d.setUTCDate(d.getUTCDate() + dias); return dataISO(d)
}

function adicionarMeses(v: string, meses: number, diaReferencia?: number | null) {
  const d = parseISOData(v)
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + meses
  const primeiro = new Date(Date.UTC(y, m, 1, 12))
  const ultimo = new Date(Date.UTC(primeiro.getUTCFullYear(), primeiro.getUTCMonth() + 1, 0, 12)).getUTCDate()
  const dia = Math.min(Math.max(1, Number(diaReferencia || d.getUTCDate())), ultimo)
  return dataISO(new Date(Date.UTC(primeiro.getUTCFullYear(), primeiro.getUTCMonth(), dia, 12)))
}

function adicionarAnos(v: string, anos: number) {
  const d = parseISOData(v), y = d.getUTCFullYear() + anos, m = d.getUTCMonth(), dia = d.getUTCDate()
  const ultimo = new Date(Date.UTC(y, m + 1, 0, 12)).getUTCDate()
  return dataISO(new Date(Date.UTC(y, m, Math.min(dia, ultimo), 12)))
}

function proximaDataRecorrencia(atual: string, frequencia: string, diaReferencia?: number | null, intervaloDias?: number | null) {
  switch (String(frequencia).toUpperCase()) {
    case 'MENSAL': return adicionarMeses(atual, 1, diaReferencia)
    case 'SEMANAL': return adicionarDias(atual, 7)
    case 'QUINZENAL': return adicionarDias(atual, 15)
    case 'ANUAL': return adicionarAnos(atual, 1)
    case 'PERSONALIZADA': return adicionarDias(atual, Math.max(1, Number(intervaloDias || 1)))
    default: throw new Error(`Frequência inválida: ${frequencia}`)
  }
}

async function salvarRecorrencia(baseId: string, form: RecorrenciaForm) {
  const freq = String(form.frequencia || 'MENSAL').toUpperCase()
  if (!['MENSAL', 'SEMANAL', 'QUINZENAL', 'ANUAL', 'PERSONALIZADA'].includes(freq)) throw new Error('Frequência de recorrência inválida.')
  if (!form.descricao.trim()) throw new Error('Informe a descrição.')
  if (!form.data_inicio) throw new Error('Informe a data inicial.')
  if (!(Number(form.valor) > 0)) throw new Error('Informe um valor maior que zero.')
  if (freq === 'PERSONALIZADA' && !(Number(form.intervalo_dias) > 0)) throw new Error('Informe o intervalo em dias.')
  const payload = {
    base_id: baseId,
    tipo_lancamento: form.tipo_lancamento,
    descricao: form.descricao.trim(),
    categoria_id: form.categoria_id || null,
    subcategoria_id: form.subcategoria_id || null,
    valor: Math.abs(Number(form.valor)),
    frequencia: freq,
    dia_referencia: freq === 'PERSONALIZADA' ? null : Math.min(31, Math.max(1, Number(form.dia_referencia || parseISOData(form.data_inicio).getUTCDate()))),
    intervalo_dias: freq === 'PERSONALIZADA' ? Math.max(1, Number(form.intervalo_dias || 1)) : 0,
    data_inicio: form.data_inicio,
    data_fim: form.data_fim || null,
    conta_id: form.conta_id || null,
    pj: !!form.pj,
    ativa: form.ativa !== false,
    observacao: form.observacao?.trim() || null,
    atualizado_em: new Date().toISOString(),
  }
  let id = form.id
  if (id) {
    const { data: anterior, error: consultaError } = await dataClient.from('recorrencias').select('*')
      .eq('id', id).eq('base_id', baseId).maybeSingle()
    if (consultaError || !anterior) throw new Error('Recorrência não localizada. Nenhum lançamento foi alterado.')
    const mudouAgenda = String(anterior.frequencia||'').toUpperCase() !== freq
      || Number(anterior.dia_referencia||0) !== Number(payload.dia_referencia||0)
      || Number(anterior.intervalo_dias||0) !== Number(payload.intervalo_dias||0)
      || String(anterior.data_inicio||'').slice(0,10) !== String(payload.data_inicio||'').slice(0,10)
      || String(anterior.data_fim||'').slice(0,10) !== String(payload.data_fim||'').slice(0,10)
      || Boolean(anterior.ativa) !== Boolean(payload.ativa)
    if (mudouAgenda) throw new Error('A alteração automática das parcelas permite mudar valor e demais dados, mas não frequência, datas ou ativação. Para mudar a agenda, encerre esta regra e crie uma nova.')
    const camposPendentes = {
      tipo: payload.tipo_lancamento,
      descricao: payload.descricao,
      categoria_id: payload.categoria_id,
      subcategoria_id: payload.subcategoria_id,
      valor_previsto: payload.valor,
      conta_prevista_id: payload.conta_id,
      observacoes: payload.observacao,
      pj: payload.pj,
      atualizado_em: payload.atualizado_em,
    }
    // Somente registros desta regra e ainda pendentes são atualizados.
    // Pagos/recebidos e vencimentos/competências permanecem intocados.
    const { data: atualizada, error } = await dataClient.from('recorrencias').update(payload)
      .eq('id', id).eq('base_id', baseId).select('id').maybeSingle()
    if (error) throw error
    if (!atualizada) throw new Error('A recorrência não foi atualizada. Verifique sua permissão; nenhum lançamento foi alterado.')
    const { error: parcelasError } = await dataClient.from('lancamentos').update(camposPendentes)
      .eq('base_id', baseId).eq('recorrencia_id', id).eq('status', 'PENDENTE')
    if (parcelasError) throw new Error(`A regra foi atualizada, mas os lançamentos pendentes NÃO foram atualizados: ${parcelasError.message}. Não salve novamente; confira a Auditoria.`)
  } else {
    const { data, error } = await dataClient.from('recorrencias').insert({ ...payload, criado_em: new Date().toISOString(), encerrada_em: null }).select('id').single()
    if (error) throw error
    id = data.id
  }
  await sincronizarRecorrencias(baseId, id)
  return id
}

async function sincronizarRecorrencias(baseId: string, apenasId?: string) {
  const hoje = isoHojeLocal()
  const horizonte = adicionarMeses(hoje, 6, parseISOData(hoje).getUTCDate())
  let q = dataClient.from('recorrencias').select('*').eq('base_id', baseId).eq('ativa', true)
  if (apenasId) q = q.eq('id', apenasId)
  const { data: recs, error } = await q
  if (error) throw error
  if (!recs?.length) return 0

  const ids = recs.map(r => r.id)
  const { data: existentes, error: e2 } = await dataClient.from('lancamentos').select('recorrencia_id,recorrencia_referencia,status').eq('base_id', baseId).in('recorrencia_id', ids)
  if (e2) throw e2
  // Registros cancelados também ocupam a chave UNIQUE; não fingir que uma
  // inserção com ignoreDuplicates irá recriá-los. A recuperação é auditada à parte.
  const chaves = new Set((existentes || []).map(x => `${x.recorrencia_id}|${x.recorrencia_referencia || ''}`))
  const rows: Record<string, unknown>[] = []

  for (const r of recs) {
    let data = String(r.data_inicio || '')
    if (!data) continue
    let guard = 0
    while (data < hoje && guard++ < 5000) data = proximaDataRecorrencia(data, r.frequencia, r.dia_referencia, r.intervalo_dias)
    guard = 0
    while (data <= horizonte && guard++ < 1000) {
      if (r.data_fim && data > r.data_fim) break
      const referencia = data
      const chave = `${r.id}|${referencia}`
      if (!chaves.has(chave)) {
        rows.push({
          base_id: baseId,
          
          tipo: r.tipo_lancamento,
          descricao: r.descricao,
          categoria_id: r.categoria_id || null,
          subcategoria_id: r.subcategoria_id || null,
          competencia: `${data.slice(0, 7)}-01`,
          vencimento: data,
          valor_previsto: Math.abs(Number(r.valor || 0)),
          valor_realizado: 0,
          status: 'PENDENTE',
          data_realizacao: null,
          conta_prevista_id: r.conta_id || null,
          conta_realizada_id: null,
          observacoes: r.observacao || null,
          pj: !!r.pj,
          recorrencia_id: r.id,
          recorrencia_referencia: referencia,
          origem: 'RECORRENCIA_V4',
          criado_em: new Date().toISOString(),
          atualizado_em: new Date().toISOString(),
        })
        chaves.add(chave)
      }
      data = proximaDataRecorrencia(data, r.frequencia, r.dia_referencia, r.intervalo_dias)
    }
  }
  if (rows.length) {
    /*
     * Proteção contra concorrência:
     * o banco possui índice UNIQUE parcial para
     * (base_id, recorrencia_id, recorrencia_referencia).
     *
     * Duas sincronizações podem chegar aqui simultaneamente. O Set acima
     * reduz trabalho, mas a garantia definitiva é do PostgreSQL.
     * ignoreDuplicates evita que uma segunda requisição gere cópias.
     */
    const { data: inseridos, error: insertError } = await dataClient
      .from('lancamentos')
      .upsert(rows, {
        onConflict: 'base_id,recorrencia_id,recorrencia_referencia',
        ignoreDuplicates: true,
      })
      .select('id')

    if (insertError) throw insertError
    return inseridos?.length || 0
  }
  return 0
}

async function encerrarRecorrencia(baseId: string, id: string, dataEncerramento: string) {
  const agora = new Date().toISOString()
  const { error } = await dataClient.from('recorrencias').update({ ativa: false, data_fim: dataEncerramento, encerrada_em: agora, atualizado_em: agora }).eq('id', id).eq('base_id', baseId)
  if (error) throw error
  const { error: lancError } = await dataClient.from('lancamentos').update({ status: 'CANCELADO', atualizado_em: agora }).eq('base_id', baseId).eq('recorrencia_id', id).eq('status', 'PENDENTE').gte('vencimento', dataEncerramento)
  if (lancError) throw lancError
}

async function reativarRecorrencia(baseId: string, id: string) {
  const { error } = await dataClient.from('recorrencias').update({ ativa: true, data_fim: null, encerrada_em: null, atualizado_em: new Date().toISOString() }).eq('id', id).eq('base_id', baseId)
  if (error) throw error
  await sincronizarRecorrencias(baseId, id)
}

async function listarCartoes(baseId: string): Promise<import('../src/types/database').CartaoCredito[]> {
  const { data, error } = await dataClient.from('cartoes').select('*').eq('base_id', baseId).order('ativo', { ascending: false }).order('nome', { ascending: true })
  if (error) throw error
  return (data || []) as import('../src/types/database').CartaoCredito[]
}

function competenciaAdicionarMeses(competencia: string, quantidade: number) {
  const [ano, mes] = competencia.slice(0, 7).split('-').map(Number)
  const d = new Date(Date.UTC(ano, mes - 1 + quantidade, 1, 12))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

function vencimentoDaCompetencia(competencia: string, diaVencimento: number) {
  const [ano, mes] = competencia.slice(0, 7).split('-').map(Number)
  const ultimo = new Date(Date.UTC(ano, mes, 0, 12)).getUTCDate()
  const dia = Math.min(Math.max(1, Number(diaVencimento || 1)), ultimo)
  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
}

async function faturaCartaoQuitada(
  baseId: string,
  fatura: { id: string; status?: string | null; lancamento_id?: string | null },
): Promise<boolean> {
  const [principal, vinculados] = await Promise.all([
    fatura.lancamento_id
      ? dataClient.from('lancamentos').select('id,status').eq('base_id', baseId).eq('id', fatura.lancamento_id).maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    dataClient.from('lancamentos').select('id,status').eq('base_id', baseId).eq('fatura_id', fatura.id).limit(100),
  ])
  if (principal.error || vinculados.error) throw new Error('Não foi possível verificar se a fatura já foi baixada.')
  if (String(principal.data?.status || '').toUpperCase() === 'PAGO') return true
  if ((vinculados.data || []).some(l => String(l.status || '').toUpperCase() === 'PAGO')) return true
  if (principal.data || (vinculados.data || []).length) return false
  return String(fatura.status || '').toUpperCase() === 'PAGA'
}

async function obterOuCriarFaturaAbertaCartao(baseId: string, cartao: CartaoCredito, competencia: string) {
  if (!/^\d{4}-\d{2}$/.test(competencia || '')) throw new Error('Competência da fatura inválida.')
  const { data: existente, error: buscaErro } = await dataClient
    .from('faturas')
    .select('id,status,valor,vencimento,lancamento_id,competencia')
    .eq('base_id', baseId)
    .eq('cartao_id', cartao.id)
    .eq('competencia', competencia)
    .limit(1)
  if (buscaErro) throw new Error(`Falha ao localizar a fatura ${competencia}: ${buscaErro.message}`)
  if (existente?.[0]) {
    if (await faturaCartaoQuitada(baseId, existente[0])) {
      throw new Error(`A fatura ${competencia} já está paga e não pode receber novas compras.`)
    }
    return existente[0]
  }
  const vencimento = vencimentoDaCompetencia(competencia, Number(cartao.dia_vencimento || 1))
  const { data, error } = await dataClient.from('faturas').insert({
    base_id: baseId,
    cartao_id: cartao.id,
    competencia,
    vencimento,
    valor: 0,
    status: 'ABERTA',
  }).select('id,status,valor,vencimento,lancamento_id,competencia').single()
  if (error) throw new Error(`Falha ao criar a fatura ${competencia}: ${error.message}`)
  return data
}

async function ajustarValorFaturaCartao(baseId: string, faturaId: string, delta: number, cartao?: CartaoCredito) {
  const { data: fatura, error: buscaErro } = await dataClient
    .from('faturas')
    .select('id,cartao_id,competencia,vencimento,valor,status,lancamento_id')
    .eq('id', faturaId)
    .eq('base_id', baseId)
    .single()
  if (buscaErro || !fatura) throw new Error('Fatura vinculada à compra não localizada.')
  if (await faturaCartaoQuitada(baseId, fatura)) throw new Error('Não é possível alterar uma fatura já paga.')
  const novoValor = Math.max(0, Number((Number(fatura.valor || 0) + Number(delta || 0)).toFixed(2)))
  const { error: updateErro } = await dataClient.from('faturas').update({ valor: novoValor }).eq('id', faturaId).eq('base_id', baseId)
  if (updateErro) throw new Error(`Falha ao atualizar o valor da fatura: ${updateErro.message}`)
  if (novoValor <= 0) {
    if (fatura.lancamento_id) {
      const { data: lancamento } = await dataClient.from('lancamentos').select('id,status').eq('id', fatura.lancamento_id).eq('base_id', baseId).maybeSingle()
      if (lancamento && !['PAGO','RECEBIDO'].includes(String(lancamento.status || '').toUpperCase())) {
        const { error: unlinkError } = await dataClient.from('faturas').update({ lancamento_id: null }).eq('id', faturaId).eq('base_id', baseId)
        if (unlinkError) throw unlinkError
        const { error: delLanc } = await dataClient.from('lancamentos').delete().eq('id', lancamento.id).eq('base_id', baseId)
        if (delLanc) throw new Error(`Falha ao remover o Contas a Pagar zerado da fatura: ${delLanc.message}`)

      }
    }
    return novoValor
  }
  const cartaoAtual = cartao || (await listarCartoes(baseId)).find(c => c.id === fatura.cartao_id)
  if (cartaoAtual && fatura.competencia && fatura.vencimento) {
    await sincronizarLancamentoFaturaCartao(baseId, fatura.id, cartaoAtual, fatura.competencia, fatura.vencimento, novoValor)
  }
  return novoValor
}

async function validarParcelasAlteraveis(baseId: string, parcelas: Array<{ fatura_id?: string | null; status?: string | null }>) {
  if (parcelas.some(p => String(p.status || '').toUpperCase() === 'PAGA')) {
    throw new Error('Há parcela já paga. Desfaça a baixa antes de alterar a estrutura da compra.')
  }
  const faturaIds = [...new Set(parcelas.map(p => p.fatura_id).filter((id): id is string => !!id))]
  if (!faturaIds.length) return
  const { data: faturas, error: faturasErro } = await dataClient
    .from('faturas')
    .select('id,status,lancamento_id')
    .eq('base_id', baseId)
    .in('id', faturaIds)
  if (faturasErro) throw new Error(`Falha ao validar faturas da compra: ${faturasErro.message}`)
  if ((await Promise.all((faturas || []).map(f => faturaCartaoQuitada(baseId, f)))).some(Boolean)) {
    throw new Error('Há parcela vinculada a fatura já paga. Desfaça a baixa antes de alterar a compra.')
  }
  const lancamentoIds = [...new Set((faturas || []).map(f => f.lancamento_id).filter((id): id is string => !!id))]
  if (!lancamentoIds.length) return
  const { data: lancamentos, error: lancErro } = await dataClient.from('lancamentos').select('id,status').eq('base_id', baseId).in('id', lancamentoIds)
  if (lancErro) throw new Error(`Falha ao validar Contas a Pagar da fatura: ${lancErro.message}`)
  if ((lancamentos || []).some(l => ['PAGO','RECEBIDO'].includes(String(l.status || '').toUpperCase()))) {
    throw new Error('Há fatura já baixada no Contas a Pagar. Desfaça a baixa antes de alterar a compra.')
  }
}

async function salvarCompraCartao(baseId: string, form: CompraCartaoForm) {
  if (!form.cartao_id) throw new Error('Selecione o cartão.')
  if (!form.data_compra) throw new Error('Informe a data da compra.')
  if (!form.descricao.trim()) throw new Error('Informe a descrição.')
  if (!(Number(form.valor_total) > 0)) throw new Error('Informe um valor maior que zero.')
  const competenciaInicial = String(form.fatura_competencia || '').slice(0, 7)
  if (!/^\d{4}-\d{2}$/.test(competenciaInicial)) throw new Error('Informe a fatura inicial da compra.')
  const qtd = Math.max(1, Math.trunc(Number(form.quantidade_parcelas || 1)))
  const cartoes = await listarCartoes(baseId)
  const cartao = cartoes.find(c => c.id === form.cartao_id)
  if (!cartao) throw new Error('Cartão não localizado.')

  const compraPayload = { base_id: baseId, cartao_id: form.cartao_id, data_compra: form.data_compra, descricao: form.descricao.trim(), valor_total: Number(form.valor_total), parcelas: qtd, categoria_id: form.categoria_id || null, subcategoria_id: form.subcategoria_id || null, observacao: form.observacao?.trim() || null, atualizado_em: new Date().toISOString(), criado_em: new Date().toISOString() }
  const { data: compra, error } = await dataClient.from('compras_cartao').insert(compraPayload).select('id').single()
  if (error) throw new Error(`Falha ao registrar compra no cartão: ${error.message}`)

  const baseValor = Math.floor((Number(form.valor_total) / qtd) * 100) / 100
  const rows = []
  const deltas = new Map<string, number>()
  for (let i = 0; i < qtd; i += 1) {
    const n = i + 1
    const valor = n === qtd ? Number((Number(form.valor_total) - baseValor * (qtd - 1)).toFixed(2)) : baseValor
    const competencia = competenciaAdicionarMeses(competenciaInicial, i)
    const fatura = await obterOuCriarFaturaAbertaCartao(baseId, cartao, competencia)
    rows.push({ base_id: baseId, compra_id: compra.id, cartao_id: form.cartao_id, numero_parcela: n, total_parcelas: qtd, fatura_id: fatura.id, vencimento_fatura: fatura.vencimento || vencimentoDaCompetencia(competencia, Number(cartao.dia_vencimento || 1)), valor, status: 'ABERTA', criado_em: new Date().toISOString() })
    deltas.set(fatura.id, Number((Number(deltas.get(fatura.id) || 0) + valor).toFixed(2)))
  }
  const { error: e2 } = await dataClient.from('parcelas_cartao').insert(rows)
  if (e2) throw new Error(`Falha ao registrar parcelas da compra: ${e2.message}`)
  for (const [faturaId, delta] of deltas) await ajustarValorFaturaCartao(baseId, faturaId, delta, cartao)
  return compra.id
}

async function atualizarCompraCartao(baseId: string, form: AtualizarCompraCartaoForm) {
  if (!form.id) throw new Error('Compra não identificada.')
  if (!form.cartao_id) throw new Error('Selecione o cartão.')
  if (!form.data_compra) throw new Error('Informe a data da compra.')
  if (!form.descricao.trim()) throw new Error('Informe a descrição.')
  if (!(Number(form.valor_total) > 0)) throw new Error('Informe um valor maior que zero.')
  const competenciaInicial = String(form.fatura_competencia || '').slice(0, 7)
  if (!/^\d{4}-\d{2}$/.test(competenciaInicial)) throw new Error('Informe a fatura inicial da compra.')

  const [{ data: compraAtual, error: compraErro }, { data: parcelasAtuais, error: parcelasErro }, cartoes] = await Promise.all([
    dataClient.from('compras_cartao').select('*').eq('id', form.id).eq('base_id', baseId).single(),
    dataClient.from('parcelas_cartao').select('*').eq('compra_id', form.id).eq('base_id', baseId).order('numero_parcela', { ascending: true }),
    listarCartoes(baseId),
  ])
  if (compraErro || !compraAtual) throw new Error('Compra não localizada.')
  if (parcelasErro) throw new Error(`Falha ao carregar parcelas da compra: ${parcelasErro.message}`)

  const cartao = cartoes.find(c => c.id === form.cartao_id)
  if (!cartao) throw new Error('Cartão não localizado.')
  const qtd = Math.max(1, Math.trunc(Number(form.quantidade_parcelas || 1)))
  const primeiraParcela = (parcelasAtuais || [])[0]
  let competenciaAtual = String(primeiraParcela?.vencimento_fatura || '').slice(0, 7)
  if (primeiraParcela?.fatura_id) {
    const { data: f } = await dataClient.from('faturas').select('competencia').eq('id', primeiraParcela.fatura_id).eq('base_id', baseId).maybeSingle()
    competenciaAtual = String(f?.competencia || competenciaAtual).slice(0, 7)
  }
  const estruturalAlterada =
    compraAtual.cartao_id !== form.cartao_id ||
    String(compraAtual.data_compra || '').slice(0,10) !== form.data_compra ||
    Math.abs(Number(compraAtual.valor_total || 0) - Number(form.valor_total)) > 0.0001 ||
    Number(compraAtual.parcelas || 1) !== qtd ||
    (!!competenciaAtual && competenciaAtual !== competenciaInicial)

  if (estruturalAlterada) await validarParcelasAlteraveis(baseId, parcelasAtuais || [])

  const agora = new Date().toISOString()
  const payload = {
    cartao_id: form.cartao_id,
    data_compra: form.data_compra,
    descricao: form.descricao.trim(),
    valor_total: Number(form.valor_total),
    parcelas: qtd,
    categoria_id: form.categoria_id || null,
    subcategoria_id: form.subcategoria_id || null,
    observacao: form.observacao?.trim() || null,
    atualizado_em: agora,
  }
  const { error: updateErro } = await dataClient.from('compras_cartao').update(payload).eq('id', form.id).eq('base_id', baseId)
  if (updateErro) throw new Error(`Falha ao atualizar compra: ${updateErro.message}`)

  if (estruturalAlterada) {
    const deltasAntigos = new Map<string, number>()
    for (const p of parcelasAtuais || []) if (p.fatura_id) deltasAntigos.set(p.fatura_id, Number((Number(deltasAntigos.get(p.fatura_id) || 0) + Number(p.valor || 0)).toFixed(2)))
    const { error: deleteErro } = await dataClient.from('parcelas_cartao').delete().eq('compra_id', form.id).eq('base_id', baseId)
    if (deleteErro) throw new Error(`Falha ao recalcular parcelas da compra: ${deleteErro.message}`)
    for (const [faturaId, valor] of deltasAntigos) await ajustarValorFaturaCartao(baseId, faturaId, -valor)

    const baseValor = Math.floor((Number(form.valor_total) / qtd) * 100) / 100
    const rows = []
    const deltasNovos = new Map<string, number>()
    for (let i = 0; i < qtd; i += 1) {
      const numero = i + 1
      const valor = numero === qtd ? Number((Number(form.valor_total) - baseValor * (qtd - 1)).toFixed(2)) : baseValor
      const competencia = competenciaAdicionarMeses(competenciaInicial, i)
      const fatura = await obterOuCriarFaturaAbertaCartao(baseId, cartao, competencia)
      rows.push({ base_id: baseId, compra_id: form.id, cartao_id: form.cartao_id, numero_parcela: numero, total_parcelas: qtd, fatura_id: fatura.id, vencimento_fatura: fatura.vencimento || vencimentoDaCompetencia(competencia, Number(cartao.dia_vencimento || 1)), valor, status: 'ABERTA', criado_em: agora })
      deltasNovos.set(fatura.id, Number((Number(deltasNovos.get(fatura.id) || 0) + valor).toFixed(2)))
    }
    const { error: insertErro } = await dataClient.from('parcelas_cartao').insert(rows)
    if (insertErro) throw new Error(`Falha ao recriar parcelas da compra: ${insertErro.message}`)
    for (const [faturaId, valor] of deltasNovos) await ajustarValorFaturaCartao(baseId, faturaId, valor, cartao)
  }
}

async function gerarParcelasAdicionaisCompraCartao(baseId: string, compraId: string, novoTotal: number) {
  const totalDesejado = Math.max(1, Math.trunc(Number(novoTotal || 1)))
  const [{ data: compra, error: compraErro }, { data: parcelas, error: parcelasErro }, cartoes] = await Promise.all([
    dataClient.from('compras_cartao').select('*').eq('id', compraId).eq('base_id', baseId).single(),
    dataClient.from('parcelas_cartao').select('*').eq('compra_id', compraId).eq('base_id', baseId).order('numero_parcela', { ascending: true }),
    listarCartoes(baseId),
  ])
  if (compraErro || !compra) throw new Error('Compra não localizada.')
  if (parcelasErro) throw new Error(`Falha ao carregar parcelas da compra: ${parcelasErro.message}`)

  const existentes = parcelas || []
  await validarParcelasAlteraveis(baseId, existentes)
  const maiorNumero = existentes.reduce((m, p) => Math.max(m, Number(p.numero_parcela || 0)), 0)
  if (totalDesejado <= maiorNumero) throw new Error(`Informe um total maior que ${maiorNumero} parcela(s).`)
  const cartao = cartoes.find(c => c.id === compra.cartao_id)
  if (!cartao) throw new Error('Cartão da compra não localizado.')

  const referencia = existentes[existentes.length - 1]
  const valorReferencia = Number(referencia?.valor || 0) > 0 ? Number(referencia.valor) : Number(compra.valor_total || 0)
  const competenciaReferencia = String(referencia?.vencimento_fatura || '').slice(0, 7) || String(compra.data_compra || '').slice(0, 7)
  const agora = new Date().toISOString()
  const novas = []
  const deltas = new Map<string, number>()
  for (let numero = maiorNumero + 1; numero <= totalDesejado; numero += 1) {
    const competencia = competenciaAdicionarMeses(competenciaReferencia, numero - maiorNumero)
    const fatura = await obterOuCriarFaturaAbertaCartao(baseId, cartao, competencia)
    novas.push({
      base_id: baseId,
      compra_id: compraId,
      cartao_id: compra.cartao_id,
      numero_parcela: numero,
      total_parcelas: totalDesejado,
      fatura_id: fatura.id,
      vencimento_fatura: fatura.vencimento || vencimentoDaCompetencia(competencia, Number(cartao.dia_vencimento || 1)),
      valor: valorReferencia,
      status: 'ABERTA',
      criado_em: agora,
    })
    deltas.set(fatura.id, Number((Number(deltas.get(fatura.id) || 0) + valorReferencia).toFixed(2)))
  }

  const { error: insertErro } = await dataClient.from('parcelas_cartao').insert(novas)
  if (insertErro) throw new Error(`Falha ao gerar novas parcelas: ${insertErro.message}`)
  const { error: totaisErro } = await dataClient.from('parcelas_cartao').update({ total_parcelas: totalDesejado }).eq('compra_id', compraId).eq('base_id', baseId)
  if (totaisErro) throw new Error(`Parcelas criadas, mas não foi possível atualizar o total: ${totaisErro.message}`)
  const { error: compraUpdateErro } = await dataClient.from('compras_cartao').update({ parcelas: totalDesejado, atualizado_em: agora }).eq('id', compraId).eq('base_id', baseId)
  if (compraUpdateErro) throw new Error(`Parcelas criadas, mas não foi possível atualizar a compra: ${compraUpdateErro.message}`)
  for (const [faturaId, valor] of deltas) await ajustarValorFaturaCartao(baseId, faturaId, valor, cartao)
}

async function moverParcelaCartaoParaFatura(baseId: string, parcelaId: string, competenciaDestino: string) {
  if (!/^[0-9]{4}-[0-9]{2}$/.test(competenciaDestino || '')) throw new Error('Informe uma competência válida para a nova fatura.')
  const { data: parcela, error: parcelaErro } = await dataClient.from('parcelas_cartao').select('*').eq('id', parcelaId).eq('base_id', baseId).single()
  if (parcelaErro || !parcela) throw new Error('Parcela não localizada.')
  if (String(parcela.status || '').toUpperCase() === 'PAGA') throw new Error('Uma parcela já paga não pode ser transferida para outra fatura.')
  const cartoes = await listarCartoes(baseId)
  const cartao = cartoes.find(c => c.id === parcela.cartao_id)
  if (!cartao) throw new Error('Cartão da parcela não localizado.')
  let faturaOrigem: any = null
  if (parcela.fatura_id) {
    const { data, error } = await dataClient.from('faturas').select('*').eq('id', parcela.fatura_id).eq('base_id', baseId).single()
    if (error || !data) throw new Error('Fatura atual da parcela não localizada.')
    if (await faturaCartaoQuitada(baseId, data)) throw new Error('A fatura atual já está paga e não pode ser alterada.')
    faturaOrigem = data
    if (String(data.competencia || '').slice(0, 7) === competenciaDestino) return data.id as string
  }
  const destino = await obterOuCriarFaturaAbertaCartao(baseId, cartao, competenciaDestino)
  if (await faturaCartaoQuitada(baseId, destino)) throw new Error('A fatura de destino já está paga.')
  const vencimentoDestino = destino.vencimento || vencimentoDaCompetencia(competenciaDestino, Number(cartao.dia_vencimento || 1))
  const valor = Number(parcela.valor || 0)
  const faturaOrigemId = parcela.fatura_id as string | null
  const vencimentoOrigem = parcela.vencimento_fatura as string | null
  const { error: updateErro } = await dataClient.from('parcelas_cartao').update({ fatura_id: destino.id, vencimento_fatura: vencimentoDestino }).eq('id', parcelaId).eq('base_id', baseId)
  if (updateErro) throw new Error(`Falha ao transferir a parcela: ${updateErro.message}`)
  let origemAjustada = false
  try {
    if (faturaOrigemId) { await ajustarValorFaturaCartao(baseId, faturaOrigemId, -valor, cartao); origemAjustada = true }
    await ajustarValorFaturaCartao(baseId, destino.id, valor, cartao)
  } catch (e) {
    await dataClient.from('parcelas_cartao').update({ fatura_id: faturaOrigemId, vencimento_fatura: vencimentoOrigem }).eq('id', parcelaId).eq('base_id', baseId)
    if (origemAjustada && faturaOrigemId) { try { await ajustarValorFaturaCartao(baseId, faturaOrigemId, valor, cartao) } catch {} }
    throw e
  }
  return destino.id as string
}

async function excluirCompraCartao(
  baseId: string,
  compraId: string,
  opcoes: ExcluirCompraCartaoOpcoes = {},
): Promise<ResultadoExclusaoCompraCartao> {
  const { data: parcelas, error: parcelasErro } = await dataClient
    .from('parcelas_cartao')
    .select('id,numero_parcela,total_parcelas,fatura_id,status,valor')
    .eq('compra_id', compraId)
    .eq('base_id', baseId)
    .order('numero_parcela', { ascending: true })
  if (parcelasErro) throw new Error(`Falha ao verificar parcelas da compra: ${parcelasErro.message}`)

  const todas = parcelas || []
  const modo = opcoes.modo || 'TODAS'
  const referencia = Math.max(1, Number(opcoes.numeroParcelaReferencia || todas[0]?.numero_parcela || 1))

  const alvo = modo === 'APENAS_ATUAL'
    ? todas.filter(p => Number(p.numero_parcela || 0) === referencia)
    : modo === 'ATUAL_E_FUTURAS'
      ? todas.filter(p => Number(p.numero_parcela || 0) >= referencia)
      : todas

  if (todas.length && !alvo.length) {
    throw new Error(`A parcela ${referencia} não foi localizada para exclusão.`)
  }

  if (alvo.some(p => String(p.status || '').toUpperCase() === 'PAGA')) {
    throw new Error('Não é possível excluir parcela já paga. Primeiro desfaça a baixa da fatura correspondente.')
  }

  const faturaIds = [...new Set(alvo.map(p => p.fatura_id).filter((id): id is string => !!id))]
  if (faturaIds.length) {
    const { data: faturas, error: faturasErro } = await dataClient
      .from('faturas')
      .select('id,status,lancamento_id')
      .eq('base_id', baseId)
      .in('id', faturaIds)
    if (faturasErro) throw new Error(`Falha ao validar a fatura da parcela: ${faturasErro.message}`)

    if ((await Promise.all((faturas || []).map(f => faturaCartaoQuitada(baseId, f)))).some(Boolean)) {
      throw new Error('Não é possível excluir uma parcela pertencente a fatura já paga.')
    }

    const lancamentoIds = [...new Set((faturas || []).map(f => f.lancamento_id).filter((id): id is string => !!id))]
    if (lancamentoIds.length) {
      const { data: lancamentos, error: lancamentosErro } = await dataClient
        .from('lancamentos')
        .select('id,status')
        .eq('base_id', baseId)
        .in('id', lancamentoIds)
      if (lancamentosErro) throw new Error(`Falha ao validar o contas a pagar da fatura: ${lancamentosErro.message}`)
      if ((lancamentos || []).some(l => ['PAGO','RECEBIDO'].includes(String(l.status || '').toUpperCase()))) {
        throw new Error('Não é possível excluir uma parcela cuja fatura já foi baixada no Contas a Pagar.')
      }
    }
  }

  if (alvo.length) {
    const ids = alvo.map(p => p.id)
    const { data: excluidas, error: deleteParcelasErro } = await dataClient
      .from('parcelas_cartao')
      .delete()
      .eq('base_id', baseId)
      .in('id', ids)
      .select('id')
    if (deleteParcelasErro) throw new Error(`Falha ao excluir parcela(s) da compra: ${deleteParcelasErro.message}`)
    if ((excluidas || []).length !== ids.length) {
      throw new Error('A exclusão não foi efetivada no banco. Verifique sua permissão de exclusão e tente novamente.')
    }
  }

  const deltasFatura = new Map<string, number>()
  for (const p of alvo) if (p.fatura_id) deltasFatura.set(p.fatura_id, Number((Number(deltasFatura.get(p.fatura_id) || 0) + Number(p.valor || 0)).toFixed(2)))
  for (const [faturaId, valor] of deltasFatura) await ajustarValorFaturaCartao(baseId, faturaId, -valor)

  const { data: restantes, error: restantesErro } = await dataClient
    .from('parcelas_cartao')
    .select('id')
    .eq('compra_id', compraId)
    .eq('base_id', baseId)
  if (restantesErro) throw new Error(`Falha ao conferir a exclusão das parcelas: ${restantesErro.message}`)

  const parcelasRestantes = (restantes || []).length
  let compraExcluida = false

  if (!parcelasRestantes) {
    const { data: compraRemovida, error: deleteCompraErro } = await dataClient
      .from('compras_cartao')
      .delete()
      .eq('id', compraId)
      .eq('base_id', baseId)
      .select('id')
    if (deleteCompraErro) throw new Error(`Falha ao excluir compra: ${deleteCompraErro.message}`)
    if (!compraRemovida?.length) {
      throw new Error('As parcelas foram excluídas, mas a compra não foi removida do banco. Verifique sua permissão de exclusão.')
    }
    compraExcluida = true
  } else {
    const { error: updateErro } = await dataClient
      .from('compras_cartao')
      .update({ atualizado_em: new Date().toISOString() })
      .eq('id', compraId)
      .eq('base_id', baseId)
    if (updateErro) throw new Error(`A parcela foi excluída, mas não foi possível atualizar a compra: ${updateErro.message}`)
  }

  return { parcelasExcluidas: alvo.length, compraExcluida, parcelasRestantes }
}

function descricaoFaturaCartao(nomeCartao: string | null | undefined, competencia: string) {
  const [ano, mes] = competencia.split('-')
  return `Fatura ${nomeCartao || 'Cartão'} - ${mes}/${ano}`
}

async function sincronizarLancamentoFaturaCartao(
  baseId: string,
  faturaId: string,
  cartao: CartaoCredito,
  competencia: string,
  vencimento: string,
  valor: number,
) {
  const agora = new Date().toISOString()
  const { data: existentes, error: buscaErro } = await dataClient
    .from('lancamentos')
    .select('id,status')
    .eq('base_id', baseId)
    .eq('fatura_id', faturaId)
    .limit(1)
  if (buscaErro) throw new Error(`Falha ao localizar a conta a pagar da fatura: ${buscaErro.message}`)

  const payload = {
    tipo: 'DESPESA',
    descricao: descricaoFaturaCartao(cartao.nome, competencia),
    categoria_id: cartao.categoria_fatura_id || null,
    subcategoria_id: cartao.subcategoria_fatura_id || null,
    competencia: `${competencia}-01`,
    vencimento,
    valor_previsto: Math.abs(Number(valor || 0)),
    conta_prevista_id: cartao.conta_pagamento_id || null,
    observacoes: `Fatura de cartão vinculada automaticamente ao módulo Cartões de Crédito.`,
    pj: false,
    origem: 'CARTAO',
    cartao_id: cartao.id,
    fatura_id: faturaId,
    atualizado_em: agora,
  }

  let lancamentoId = existentes?.[0]?.id as string | undefined
  const statusAtual = String(existentes?.[0]?.status || '').toUpperCase()
  if (lancamentoId) {
    if (!['PAGO','RECEBIDO','CANCELADO'].includes(statusAtual)) {
      const { error } = await dataClient.from('lancamentos').update({ ...payload, status: 'PENDENTE' }).eq('id', lancamentoId).eq('base_id', baseId)
      if (error) throw new Error(`Falha ao atualizar a conta a pagar da fatura: ${error.message}`)
    }
  } else {
    const { data, error } = await dataClient.from('lancamentos').insert({
      base_id: baseId,
      ...payload,
      valor_realizado: 0,
      status: 'PENDENTE',
      data_realizacao: null,
      conta_realizada_id: null,
      criado_em: agora,
    }).select('id').single()
    if (error) throw new Error(`Falha ao criar a conta a pagar da fatura: ${error.message}`)
    lancamentoId = data.id as string
  }

  if (lancamentoId) {
    const { error } = await dataClient.from('faturas').update({ lancamento_id: lancamentoId }).eq('id', faturaId).eq('base_id', baseId)
    if (error) throw new Error(`Falha ao vincular a conta a pagar à fatura: ${error.message}`)
  }
}

async function sincronizarFaturasAbertasContasPagar(baseId: string) {
  const [{ data: faturas, error: faturasErro }, cartoes] = await Promise.all([
    dataClient.from('faturas').select('id,cartao_id,competencia,vencimento,valor,lancamento_id,status').eq('base_id', baseId).eq('status', 'ABERTA').is('lancamento_id', null),
    listarCartoes(baseId),
  ])
  if (faturasErro) throw new Error(`Falha ao localizar faturas sem conta a pagar: ${faturasErro.message}`)
  let sincronizadas = 0
  for (const fatura of faturas || []) {
    const cartao = cartoes.find(c => c.id === fatura.cartao_id)
    if (!cartao || !fatura.competencia || !fatura.vencimento || !(Number(fatura.valor) > 0)) continue
    if (await faturaCartaoQuitada(baseId, fatura)) continue
    await sincronizarLancamentoFaturaCartao(baseId, fatura.id, cartao, fatura.competencia, fatura.vencimento, Number(fatura.valor))
    sincronizadas += 1
  }
  return sincronizadas
}

async function salvarFaturaImportada(baseId: string, form: FaturaImportadaForm) {
  if (!form.cartao_id) throw new Error('Selecione o cartão.')
  if (!/^\d{4}-\d{2}$/.test(form.competencia || '')) throw new Error('Competência inválida da fatura.')
  const cartoes = await listarCartoes(baseId)
  const cartao = cartoes.find(c => c.id === form.cartao_id)
  if (!cartao) throw new Error('Cartão não localizado.')
  let vencimento = form.vencimento || null
  if (!vencimento) {
    const [ano, mes] = form.competencia.split('-').map(Number)
    const ultimo = new Date(Date.UTC(ano, mes, 0, 12)).getUTCDate()
    const dia = Math.min(Math.max(1, Number(cartao.dia_vencimento || 1)), ultimo)
    vencimento = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
  }
  const { data: existente, error: buscaError } = await dataClient.from('faturas').select('id,status,lancamento_id').eq('base_id', baseId).eq('cartao_id', form.cartao_id).eq('competencia', form.competencia).limit(1)
  if (buscaError) throw new Error(`Falha ao localizar a fatura: ${buscaError.message}`)
  if (existente?.[0] && await faturaCartaoQuitada(baseId, existente[0])) throw new Error('A fatura já foi paga e não pode ser reimportada ou alterada.')
  const payload = { vencimento, valor: Number(form.valor || 0), status: 'ABERTA' }
  let faturaId: string
  if (existente?.[0]?.id) {
    const { error } = await dataClient.from('faturas').update(payload).eq('id', existente[0].id).eq('base_id', baseId)
    if (error) throw new Error(`Falha ao atualizar a fatura: ${error.message}`)
    faturaId = existente[0].id as string
  } else {
    const { data, error } = await dataClient.from('faturas').insert({ base_id: baseId, cartao_id: form.cartao_id, competencia: form.competencia, ...payload }).select('id').single()
    if (error) throw new Error(`Falha ao criar a fatura: ${error.message}`)
    faturaId = data.id as string
  }
  await sincronizarLancamentoFaturaCartao(baseId, faturaId, cartao, form.competencia, vencimento, Number(form.valor || 0))
  return faturaId
}

async function salvarItemFaturaCartao(baseId: string, form: ItemFaturaCartaoForm) {
  if (!form.cartao_id) throw new Error('Selecione o cartão.')
  if (!form.data_compra) throw new Error('Data da compra inválida na fatura.')
  if (!form.descricao.trim()) throw new Error('Descrição inválida na fatura.')
  if (!(Number(form.valor) > 0)) throw new Error('Valor inválido na fatura.')

  const cartoes = await listarCartoes(baseId)
  const cartao = cartoes.find(c => c.id === form.cartao_id)
  if (!cartao) throw new Error('Cartão não localizado.')

  const competencia = (form.competencia || form.vencimento?.slice(0, 7) || form.data_compra.slice(0, 7)).slice(0, 7)
  let vencimento = form.vencimento || null
  if (!vencimento) {
    const [ano, mes] = competencia.split('-').map(Number)
    if (ano && mes) {
      const ultimo = new Date(Date.UTC(ano, mes, 0, 12)).getUTCDate()
      const dia = Math.min(Math.max(1, Number(cartao.dia_vencimento || 1)), ultimo)
      vencimento = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
    }
  }
  if (!vencimento) throw new Error('Não foi possível determinar o vencimento da fatura.')

  const agora = new Date().toISOString()
  const numeroParcela = Math.max(1, Number(form.numero_parcela || 1))
  if (form.fatura_id) {
    const { data: fatura, error } = await dataClient.from('faturas').select('id,status,lancamento_id').eq('base_id', baseId).eq('id', form.fatura_id).maybeSingle()
    if (error || !fatura) throw new Error('Fatura do item não localizada.')
    if (await faturaCartaoQuitada(baseId, fatura)) throw new Error('Uma fatura já paga não pode receber novas parcelas.')
  }
  const totalParcelas = Math.max(1, Number(form.total_parcelas || 1))

  // Quando a parcela já foi prevista numa importação anterior, apenas a vincula
  // à fatura atual. Assim a próxima fatura não duplica a mesma compra.
  if (form.parcela_existente_id) {
    const { data: prevista, error: previstaErro } = await dataClient
      .from('parcelas_cartao')
      .select('id,compra_id,cartao_id')
      .eq('id', form.parcela_existente_id)
      .eq('base_id', baseId)
      .single()
    if (previstaErro || !prevista) throw new Error('A parcela prevista não foi localizada para conciliação.')
    if (prevista.cartao_id && prevista.cartao_id !== form.cartao_id) throw new Error('A parcela prevista pertence a outro cartão.')

    const { error: parcelaErro } = await dataClient.from('parcelas_cartao').update({
      fatura_id: form.fatura_id || null,
      vencimento_fatura: vencimento,
      valor: Number(form.valor),
      status: 'ABERTA',
    }).eq('id', form.parcela_existente_id).eq('base_id', baseId)
    if (parcelaErro) throw new Error(`Falha ao conciliar a parcela prevista: ${parcelaErro.message}`)

    const { error: compraErro } = await dataClient.from('compras_cartao').update({
      categoria_id: form.categoria_id || null,
      subcategoria_id: form.subcategoria_id || null,
      atualizado_em: agora,
    }).eq('id', prevista.compra_id).eq('base_id', baseId)
    if (compraErro) throw new Error(`Falha ao atualizar a classificação da compra: ${compraErro.message}`)
    return prevista.compra_id as string
  }

  const compraPayload = {
    base_id: baseId,
    cartao_id: form.cartao_id,
    data_compra: form.data_compra,
    descricao: form.descricao.trim(),
    valor_total: Number(form.valor),
    parcelas: totalParcelas,
    categoria_id: form.categoria_id || null,
    subcategoria_id: form.subcategoria_id || null,
    observacao: form.observacao?.trim() || null,
    atualizado_em: agora,
    criado_em: agora,
  }
  const { data: compra, error } = await dataClient.from('compras_cartao').insert(compraPayload).select('id').single()
  if (error) throw new Error(`Falha ao registrar item da fatura: ${error.message}`)

  const rows = [{
    base_id: baseId,
    compra_id: compra.id,
    cartao_id: form.cartao_id,
    numero_parcela: numeroParcela,
    total_parcelas: totalParcelas,
    fatura_id: form.fatura_id || null,
    vencimento_fatura: vencimento,
    valor: Number(form.valor),
    status: 'ABERTA',
    criado_em: agora,
  }]

  const deltasFuturos = new Map<string, number>()
  if (form.gerar_parcelas_futuras && totalParcelas > numeroParcela) {
    for (let numero = numeroParcela + 1; numero <= totalParcelas; numero += 1) {
      const competenciaFutura = competenciaAdicionarMeses(competencia, numero - numeroParcela)
      const faturaFutura = await obterOuCriarFaturaAbertaCartao(baseId, cartao, competenciaFutura)
      const valorFuturo = Number(form.valor)
      rows.push({
        base_id: baseId,
        compra_id: compra.id,
        cartao_id: form.cartao_id,
        numero_parcela: numero,
        total_parcelas: totalParcelas,
        fatura_id: faturaFutura.id,
        vencimento_fatura: faturaFutura.vencimento || vencimentoDaCompetencia(competenciaFutura, Number(cartao.dia_vencimento || 1)),
        valor: valorFuturo,
        status: 'ABERTA',
        criado_em: agora,
      })
      deltasFuturos.set(faturaFutura.id, Number((Number(deltasFuturos.get(faturaFutura.id) || 0) + valorFuturo).toFixed(2)))
    }
  }

  const { error: parcelaError } = await dataClient.from('parcelas_cartao').insert(rows)
  if (parcelaError) throw new Error(`Falha ao vincular item às parcelas do cartão: ${parcelaError.message}`)
  for (const [faturaId, valorFuturo] of deltasFuturos) await ajustarValorFaturaCartao(baseId, faturaId, valorFuturo, cartao)
  return compra.id
}

async function salvarTransferenciaRecorrente(baseId:string, form:TransferenciaRecorrenciaForm) {
  const freq=String(form.frequencia||'MENSAL').toUpperCase()
  if(!['MENSAL','SEMANAL','QUINZENAL','ANUAL','PERSONALIZADA'].includes(freq)) throw new Error('Frequência inválida.')
  if(!form.data_inicio) throw new Error('Informe a data inicial.')
  if(freq==='PERSONALIZADA'&&!(Number(form.intervalo_dias)>0)) throw new Error('Informe o intervalo em dias.')
  const [contas,investimentos]=await Promise.all([listarContas(baseId),listarInvestimentos(baseId)])
  const origem=form.origem_tipo==='CONTA'?contas.find(c=>c.id===form.origem_id):investimentos.find(i=>i.id===form.origem_id)
  const destino=form.destino_tipo==='CONTA'?contas.find(c=>c.id===form.destino_id):investimentos.find(i=>i.id===form.destino_id)
  if(!origem||!destino) throw new Error('Origem ou destino não localizado.')
  if(form.origem_tipo===form.destino_tipo&&form.origem_id===form.destino_id) throw new Error('Origem e destino não podem ser iguais.')
  if(!(Number(form.valor)>0)) throw new Error('Informe um valor maior que zero.')

  const payload={
    base_id:baseId,
    origem_tipo:form.origem_tipo,
    origem_conta_id:form.origem_tipo==='CONTA'?origem.id:null,
    origem_investimento_id:form.origem_tipo==='INVESTIMENTO'?origem.id:null,
    destino_tipo:form.destino_tipo,
    destino_conta_id:form.destino_tipo==='CONTA'?destino.id:null,
    destino_investimento_id:form.destino_tipo==='INVESTIMENTO'?destino.id:null,
    valor:Math.abs(Number(form.valor)),observacao:form.observacao?.trim()||null,
    frequencia:freq,intervalo_dias:freq==='PERSONALIZADA'?Math.max(1,Number(form.intervalo_dias||1)):0,
    data_inicio:form.data_inicio,data_fim:form.data_fim||null,ativa:true,atualizado_em:new Date().toISOString()
  }
  const {data,error}=await dataClient.from('transferencias_recorrencias').insert({...payload,criado_em:new Date().toISOString()}).select('id').single()
  if(error) throw error
  await sincronizarTransferenciasRecorrentes(baseId,data.id)
  return data.id as string
}

async function sincronizarTransferenciasRecorrentes(baseId:string, apenasId?:string) {
  const hoje=isoHojeLocal()
  const horizonte=adicionarMeses(hoje,6,parseISOData(hoje).getUTCDate())
  let q=dataClient.from('transferencias_recorrencias').select('*').eq('base_id',baseId).eq('ativa',true)
  if(apenasId) q=q.eq('id',apenasId)
  const {data:recs,error}=await q
  if(error) throw error
  if(!recs?.length)return 0
  const ids=recs.map(r=>r.id)
  const {data:existentes,error:e2}=await dataClient.from('transferencias').select('recorrencia_id,recorrencia_referencia').eq('base_id',baseId).in('recorrencia_id',ids)
  if(e2) throw e2
  const chaves=new Set((existentes||[]).map(x=>`${x.recorrencia_id}|${x.recorrencia_referencia||''}`))
  const rows:any[]=[]
  for(const r of recs){
    let data=String(r.data_inicio||'');let guard=0
    while(data<hoje&&guard++<5000)data=proximaDataRecorrencia(data,r.frequencia,null,r.intervalo_dias)
    guard=0
    while(data<=horizonte&&guard++<1000){
      if(r.data_fim&&data>r.data_fim)break
      const chave=`${r.id}|${data}`
      if(!chaves.has(chave)){
        rows.push({base_id:baseId,data,data_prevista:data,data_realizacao:null,
          origem_tipo:r.origem_tipo,
          origem_conta_id:r.origem_conta_id||null,
          origem_investimento_id:r.origem_investimento_id||null,
          destino_tipo:r.destino_tipo,
          destino_conta_id:r.destino_conta_id||null,
          destino_investimento_id:r.destino_investimento_id||null,
          valor:Number(r.valor),status:'PENDENTE',observacao:r.observacao,recorrencia_id:r.id,recorrencia_referencia:data,
          criado_em:new Date().toISOString(),atualizado_em:new Date().toISOString()})
        chaves.add(chave)
      }
      data=proximaDataRecorrencia(data,r.frequencia,null,r.intervalo_dias)
    }
  }
  if(rows.length){
    const {data,error}=await dataClient.from('transferencias').upsert(rows,{onConflict:'base_id,recorrencia_id,recorrencia_referencia',ignoreDuplicates:true}).select('id')
    if(error)throw error
    return data?.length||0
  }
  return 0
}
return {salvarFaturaImportada,listarCartoes,faturaCartaoQuitada,sincronizarLancamentoFaturaCartao,descricaoFaturaCartao,salvarItemFaturaCartao,competenciaAdicionarMeses,obterOuCriarFaturaAbertaCartao,vencimentoDaCompetencia,ajustarValorFaturaCartao,sincronizarFaturasAbertasContasPagar,salvarLancamento,salvarCompraCartao,atualizarCompraCartao,validarParcelasAlteraveis,gerarParcelasAdicionaisCompraCartao,moverParcelaCartaoParaFatura,excluirCompraCartao,salvarRecorrencia,parseISOData,sincronizarRecorrencias,isoHojeLocal,adicionarMeses,dataISO,proximaDataRecorrencia,adicionarDias,adicionarAnos,encerrarRecorrencia,reativarRecorrencia,atualizarLancamentoRecorrenteFuturos,salvarTransferenciaRecorrente,listarContas,listarInvestimentos,sincronizarTransferenciasRecorrentes}
}
